// frontend/src/components/support/SupportChatDrawer.jsx
import React, { useMemo, useState } from "react";
import { X, Send, Bot, ExternalLink, Loader2, ThumbsUp, ThumbsDown, LifeBuoy } from "lucide-react";
import { sendSupportMessage, sendSupportFeedback } from "../../services/supportService";

export default function SupportChatDrawer({
  isOpen,
  onClose,
  currentRoute,
  currentModule,
  pageTitle,
}) {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Olá! Posso ajudar com dúvidas sobre o uso do OdontoAssist AI. Descreva o que você quer fazer na tela atual.",
      sources: [],
      suggested_actions: [],
      chain_id: null,
      provider_used: null,
      model_used: null,
      feedbackSent: false,
    },
  ]);
  const [loading, setLoading] = useState(false);

  const placeholder = useMemo(() => {
    if (pageTitle) return `Pergunte algo sobre ${pageTitle}...`;
    if (currentModule) return `Pergunte algo sobre ${currentModule}...`;
    return "Digite sua dúvida sobre o sistema...";
  }, [pageTitle, currentModule]);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setLoading(true);

    try {
      const data = await sendSupportMessage({
        message: trimmed,
        current_route: currentRoute,
        current_module: currentModule,
        page_title: pageTitle,
      });

      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.answer || "Não foi possível gerar uma resposta agora.",
        sources: data.sources || [],
        suggested_actions: data.suggested_actions || [],
        chain_id: data.chain_id || null,
        provider_used: data.provider_used || null,
        model_used: data.model_used || null,
        question: trimmed,
        feedbackSent: false,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        id: `assistant-error-${Date.now()}`,
        role: "assistant",
        content:
          "Não foi possível consultar o suporte agora. Tente novamente em instantes.",
        sources: [],
        suggested_actions: [
          {
            label: "Abrir Central de Ajuda",
            url: "/ajuda/tutoriais",
            type: "help_center",
          },
        ],
        chain_id: null,
        provider_used: null,
        model_used: null,
        question: trimmed,
        feedbackSent: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = async (msg, helpful) => {
    if (!msg || msg.feedbackSent) return;

    try {
      await sendSupportFeedback({
        chain_id: msg.chain_id,
        question: msg.question || "",
        answer: msg.content || "",
        helpful,
        current_route: currentRoute,
        current_module: currentModule,
        page_title: pageTitle,
        provider_used: msg.provider_used,
        model_used: msg.model_used,
        sources: msg.sources || [],
      });

      setMessages((prev) =>
        prev.map((item) =>
          item.id === msg.id
            ? { ...item, feedbackSent: true, feedbackHelpful: helpful }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao enviar feedback do suporte:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <Bot size={20} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Suporte IA</h3>
              <p className="text-xs text-slate-500">
                Ajuda sobre o uso do sistema
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Fechar suporte"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 bg-white text-xs text-slate-500">
          <div><strong>Rota:</strong> {currentRoute || "não informada"}</div>
          <div><strong>Tela:</strong> {pageTitle || currentModule || "não informada"}</div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-slate-50">
          {messages.map((msg) => (
            <div key={msg.id}>
              <div
                className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                  msg.role === "user"
                    ? "ml-auto bg-indigo-600 text-white"
                    : "bg-white text-slate-800 border border-slate-200"
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>

              {msg.role === "assistant" && Array.isArray(msg.suggested_actions) && msg.suggested_actions.length > 0 && (
                <div className="mt-2 ml-1 flex flex-col gap-2">
                  {msg.suggested_actions.map((action, idx) => (
                    <a
                      key={`${msg.id}-action-${idx}`}
                      href={action.url}
                      className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900 font-medium"
                    >
                      <ExternalLink size={14} />
                      {action.label}
                    </a>
                  ))}
                </div>
              )}

              {msg.role === "assistant" && !msg.feedbackSent && msg.question && (
                <div className="mt-3 ml-1 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Isso ajudou?</span>

                  <button
                    type="button"
                    onClick={() => handleFeedback(msg, true)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    <ThumbsUp size={14} />
                    Sim
                  </button>

                  <button
                    type="button"
                    onClick={() => handleFeedback(msg, false)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    <ThumbsDown size={14} />
                    Não
                  </button>
                </div>
              )}

              {msg.role === "assistant" && msg.feedbackSent && msg.question && (
                <div className="mt-3 ml-1 text-xs text-slate-500">
                  Feedback registrado.
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="max-w-[90%] rounded-2xl px-4 py-3 text-sm bg-white text-slate-700 border border-slate-200 shadow-sm flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Buscando resposta...
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-4 py-3">
          <a
            href="/contato"
            className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900 font-medium"
          >
            <LifeBuoy size={16} />
            Falar com um humano
          </a>
        </div>

        <div className="border-t border-slate-200 bg-white p-4">
          <div className="flex items-end gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={3}
              className="flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            />
            <button
              onClick={handleSend}
              disabled={loading || !message.trim()}
              className="h-[52px] w-[52px] shrink-0 rounded-2xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Enviar mensagem"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}